import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { checkPermissionTopic } from '../utils';
import { UserContext } from '../functions/Auth';

// component
import TopicCardContent from './TopicCardContent';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Stack from '@mui/material/Stack';

// types
type TopicCardProps = {
    body: string,
    commentCount: number,
    isShowCardActions: boolean,
    postStatus: number, // 投稿ステータス(下書き:0 / 投稿:1)
    postTime: string,
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenConfirmDialog?: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetEditTopic: React.Dispatch<React.SetStateAction<number>>,
    setTargetDeleteTopic?: React.Dispatch<React.SetStateAction<number>>,
    setTopicType?: React.Dispatch<React.SetStateAction<"draft" | "published" | "new">>,
    title: string,
    topicId: number,
    topicMembers: null | { // openRangeが1（公開）の場合はnull
        email: string,
        userId: number,
        userName: string,
    }[],
    updatedTime: string,
    userId: number,
    userName: string,
}

export default function TopicCard(props: TopicCardProps) {
    const {
        body,
        commentCount,
        isShowCardActions,
        postStatus,
        postTime,
        setIsEdit,
        setIsOpenConfirmDialog,
        setIsOpenModal,
        setTargetEditTopic,
        setTargetDeleteTopic,
        setTopicType,
        title,
        topicId,
        topicMembers,
        updatedTime,
        userId,
        userName,
    } = props;

    const params = useParams();
    const roomId = params.roomId;
    const userInfo = React.useContext(UserContext);
    const loginUserId = userInfo?.userId as number;
    const navigate = useNavigate();

    // トピックページアクセス権限
    const hasPermissionTopic = checkPermissionTopic(userId, topicMembers, loginUserId);

    return (
        <Card sx={{
            width: "calc(100% - 16px)",
            maxWidth: 980,
            mx: "auto",
        }}>
            {!isShowCardActions ? (<>
                {/* ルームページ */}
                <Button
                    disabled={!hasPermissionTopic}
                    onClick={() => navigate(`/${roomId}/topic/${topicId}`)}
                    sx={{
                        display: "block",
                        width: "100%",
                        p: 2,
                        textAlign: "left",
                        color: (theme) => theme.palette.grey[900],
                        '&.Mui-disabled': {
                            color: (theme) => theme.palette.grey[900],
                        },
                        '& > *:last-child': {
                            paddingBottom: 0,
                            marginBottom: 0,
                        }
                    }}
                    disableRipple
                >
                    <TopicCardContent
                        body={body}
                        commentCount={commentCount}
                        postTime={postTime}
                        title={title}
                        topicMembers={topicMembers}
                        updatedTime={updatedTime}
                        userName={userName}
                    />
                </Button>
            </>) : (
                <Box sx={{p: 2}}>
                    {/* トピック管理ページ */}
                    <TopicCardContent
                        body={body}
                        commentCount={commentCount}
                        postTime={postTime}
                        title={title}
                        topicMembers={topicMembers}
                        updatedTime={updatedTime}
                        userName={userName}
                    />
                    <CardActions sx={{ p: 0 }}>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                width: "100%",
                                maxWidth: 576,
                                mx: "auto",
                                '& > .MuiButtonBase-root': {
                                    width: 'calc(50% - 8px)',
                                }
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setTargetEditTopic(topicId);
                                    setIsEdit(true);
                                    setIsOpenModal(true);
                                    if (setTopicType) {
                                        setTopicType(postStatus === 0 ? "draft" : "published"); // 投稿ステータス(下書き:0 / 投稿:1)
                                    }
                                }}
                            >編集</Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (setTargetDeleteTopic) setTargetDeleteTopic(topicId);
                                    if (setIsOpenConfirmDialog) setIsOpenConfirmDialog(true);
                                }}
                            >削除</Button>
                        </Stack>
                    </CardActions>
                </Box>
            )}
        </Card>
    );
}